import { useEffect, useState } from "react";
import {
  Alert, Chip, CircularProgress, Grid, Icon, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { del, get, post } from "utils/WebService";
import { trechoDaChave } from "views/tools/usoIa/iaUtils";

// Chaves da OpenAI (Parametrização do Sistema > OpenAI): a chave de uso, com que o sistema chama a IA (sugestão de texto,
// resumos, relatório por frase), e a chave de administrador, só para ler o consumo e o custo reais na tela Uso da IA.
// As duas ficam criptografadas no banco e nunca voltam para a tela (em branco = mantém a gravada). "Testar" confere as
// duas e acha, entre as chaves da organização, qual é a do sistema (pelo trecho que a OpenAI mostra de cada uma).
export default function OpenAiConfig() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = coresDoTema(darkMode);

  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ chave: "", chaveAdmin: "" });
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);
  const [escolhendo, setEscolhendo] = useState(null);

  const carregar = () => {
    get("/openai/config")
      .then(setInfo)
      .catch((e) => errorAlert(e?.message || "Erro ao carregar a configuração da OpenAI."));
  };

  useEffect(carregar, []);

  const testar = () => {
    setTestando(true);
    setTeste(null);
    post("/openai/testar", {})
      .then((res) => {
        setTeste(res);
        if (res.config) setInfo(res.config);
      })
      .catch((e) => errorAlert(e?.message || "Erro ao testar as chaves."))
      .finally(() => setTestando(false));
  };

  const salvar = () => {
    setSalvando(true);
    post("/openai/config", form)
      .then((res) => {
        setInfo(res);
        setForm({ chave: "", chaveAdmin: "" });
        successAlert("Chave(s) da OpenAI gravada(s). Testando...");
        testar();
      })
      .catch((e) => errorAlert(e?.message || "Erro ao gravar as chaves."))
      .finally(() => setSalvando(false));
  };

  const apagar = (qual) => {
    const texto = qual === "uso"
      ? "Apagar a chave de uso da OpenAI? Até gravar outra, a sugestão de texto, os resumos e o relatório por frase não funcionam."
      : "Apagar a chave de administrador? A tela Uso da IA continua com o registro do sistema, sem o custo real da OpenAI.";
    infoAviso(texto, () => {
      del(`/openai/config?qual=${qual}`)
        .then((res) => {
          setInfo(res);
          setTeste(null);
          successAlert("Chave apagada.");
        })
        .catch((e) => errorAlert(e?.message || "Erro ao apagar a chave."));
    });
  };

  const escolher = (c) => {
    setEscolhendo(c.chaveId);
    post("/openai/chave-do-sistema", { projetoId: c.projetoId, chaveId: c.chaveId })
      .then((res) => {
        setInfo(res);
        setTeste((t) => (t ? { ...t, chaves: t.chaves.map((x) => ({ ...x, escolhida: x.chaveId === c.chaveId })) } : t));
        successAlert(`A chave "${c.nome}" ficou como a do sistema na tela Uso da IA.`);
      })
      .catch((e) => errorAlert(e?.message || "Erro ao escolher a chave."))
      .finally(() => setEscolhendo(null));
  };

  const rotulo = (texto) => <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{texto}</InputLabel>;
  const ajuda = (texto) => (
    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>{texto}</MDTypography>
  );
  const cab = { fontWeight: "bold", fontSize: "0.72rem", backgroundColor: cores.painel, color: cores.texto, py: 1, whiteSpace: "nowrap" };
  const cel = { fontSize: "0.78rem", color: cores.texto, verticalAlign: "top", pt: 1.5 };

  if (!info) {
    return (
      <MDBox display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} color="info" />
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ background: cores.painel, border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 2 }}>
      <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mb: 2 }}>
        A IA do sistema (sugestão de texto da ocorrência, resumo do histórico e de documento, relatório por frase) usa a OpenAI. O uso, os
        limites e o custo ficam em Ferramentas &gt; Uso da IA.
      </MDTypography>
      {info.origemChave === "PROPERTIES" && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          A chave de uso ainda vem do application.properties (em texto, no Git). Grave aqui uma chave nova da OpenAI e apague a antiga lá na OpenAI.
        </Alert>
      )}
      {!info.temChave && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          Sem chave de uso: a sugestão de texto, os resumos e o relatório por frase não funcionam.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao, height: "100%" }}>
            {rotulo("Chave de uso (API key do projeto)")}
            <MDInput size="small" fullWidth type="password" value={form.chave} onChange={(e) => setForm({ ...form, chave: e.target.value })}
              autoComplete="new-password" placeholder={info.temChave ? `Gravada, final ${info.fimChave || "?"}` : "sk-proj-... ou sk-svcacct-..."} />
            {ajuda("Com ela o sistema chama a IA. Na OpenAI: Dashboard > API keys (de preferência a chave de uma conta de serviço do projeto). Fica criptografada e não volta para a tela; em branco, mantém a gravada.")}
            {info.temChave && info.origemChave === "PARAMETRIZACAO" && (
              <MDButton variant="text" color="error" size="small" onClick={() => apagar("uso")} sx={{ mt: 0.5 }}>
                <Icon sx={{ mr: 0.5 }}>delete</Icon>Apagar chave de uso
              </MDButton>
            )}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao, height: "100%" }}>
            {rotulo("Chave de administrador (só leitura do consumo)")}
            <MDInput size="small" fullWidth type="password" value={form.chaveAdmin} onChange={(e) => setForm({ ...form, chaveAdmin: e.target.value })}
              autoComplete="new-password" placeholder={info.temChaveAdmin ? `Gravada, final ${info.fimChaveAdmin || "?"}` : "sk-admin-..."} />
            {ajuda("Para o custo e o consumo reais na tela Uso da IA (Organization > Admin keys). O sistema só lê com ela: não cria nem muda nada na OpenAI. Fica criptografada e não volta para a tela; em branco, mantém a gravada.")}
            {info.temChaveAdmin && (
              <MDButton variant="text" color="error" size="small" onClick={() => apagar("admin")} sx={{ mt: 0.5 }}>
                <Icon sx={{ mr: 0.5 }}>delete</Icon>Apagar chave de administrador
              </MDButton>
            )}
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={salvar}
              disabled={salvando || (!form.chave.trim() && !form.chaveAdmin.trim())}>
              {salvando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>save</Icon>}
              {salvando ? "Salvando..." : "Salvar"}
            </MDButton>
            <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38 }} onClick={testar} disabled={testando || (!info.temChave && !info.temChaveAdmin)}>
              {testando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>verified_user</Icon>}
              {testando ? "Testando..." : "Testar"}
            </MDButton>
          </MDBox>
          {ajuda(`Chave do sistema na OpenAI: ${trechoDaChave(info.chaveDescricao) || "ainda não identificada (Testar com a chave de administrador gravada)"}.`)}
        </Grid>
        {teste && (
          <Grid item xs={12}>
            <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao }}>
              <Alert severity={teste.usoOk ? "success" : "error"} sx={{ mb: 1, fontSize: "0.85rem" }}>
                {teste.usoOk ? "Chave de uso aceita pela OpenAI." : `Chave de uso: ${teste.usoErro}`}
              </Alert>
              <Alert severity={teste.adminOk ? "success" : "warning"} sx={{ mb: teste.chaves ? 1 : 0, fontSize: "0.85rem" }}>
                {teste.adminOk
                  ? teste.achadas === 1
                    ? "Chave de administrador aceita, e a chave do sistema foi achada entre as da organização."
                    : teste.achadas > 1
                      ? "Chave de administrador aceita, mas mais de uma chave da organização bate com a do sistema: escolha a certa abaixo."
                      : "Chave de administrador aceita, mas nenhuma chave da organização bate com a do sistema (ela é de outra organização?). Escolha abaixo, se for o caso."
                  : `Chave de administrador: ${teste.adminErro}`}
              </Alert>
              {teste.chaves && teste.chaves.length > 0 && (
                <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell sx={cab}>Projeto</TableCell>
                        <TableCell sx={cab}>Chave</TableCell>
                        <TableCell sx={cab}>Dono</TableCell>
                        <TableCell sx={cab}>Último uso</TableCell>
                        <TableCell sx={cab} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teste.chaves.map((c) => {
                        const doSistema = info.chaveId ? c.chaveId === info.chaveId : c.doSistema;
                        return (
                          <TableRow key={`${c.projetoId}-${c.chaveId}`} hover>
                            <TableCell sx={cel}>{c.projetoNome}</TableCell>
                            <TableCell sx={cel}>
                              <b>{c.nome}</b>
                              <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>{trechoDaChave(c.redacted)}</MDTypography>
                            </TableCell>
                            <TableCell sx={cel}>{c.dono || "-"}</TableCell>
                            <TableCell sx={cel}>{c.ultimoUso || "-"}</TableCell>
                            <TableCell sx={{ ...cel, pt: 1 }} align="right">
                              {doSistema ? (
                                <Chip size="small" color="success" variant="outlined" label="Chave do sistema" sx={{ height: 22, fontSize: "0.7rem" }} />
                              ) : (
                                <MDButton variant="text" color="info" size="small" onClick={() => escolher(c)} disabled={!!escolhendo}>
                                  {escolhendo === c.chaveId ? "..." : "É a do sistema"}
                                </MDButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MDBox>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );
}
